import { accentScope } from "@ui/lib/accent-scope";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  ToggleButton as RACToggleButton,
  type ToggleButtonProps as RACToggleButtonProps,
} from "@ui/primitives/ToggleButton";
import type { ReactNode } from "react";
import { type ToggleTileColor, type ToggleTileOrientation, toggleTile } from "./styles";

export interface ToggleTileProps extends Omit<RACToggleButtonProps, "children"> {
  /** Accent colour for hover/selected states — pick the one that matches the icon. */
  color?: ToggleTileColor;
  /** Leading icon. Sized by orientation (40px vertical, 32px horizontal). */
  icon: ReactNode;
  /** The caption / accessible label. */
  label: ReactNode;
  /** `vertical` = square card, caption below. `horizontal` = inline pill. */
  orientation?: ToggleTileOrientation;
}

export function ToggleTile({
  color = "neutral",
  icon,
  label,
  orientation = "horizontal",
  className,
  ...props
}: ToggleTileProps) {
  const slots = toggleTile({ color, orientation });
  const isVertical = orientation === "vertical";

  return (
    <RACToggleButton
      {...props}
      {...accentScope(color)}
      className={composeRenderProps(className, (resolved) => slots.root({ className: resolved }))}
    >
      {(renderProps) => {
        const surface = (
          <span className={slots.surface({ isHovered: renderProps.isHovered, isSelected: renderProps.isSelected })}>
            <span aria-hidden="true" className={slots.icon()}>
              {icon}
            </span>
            {!isVertical && (
              <span className={slots.caption({ isHovered: renderProps.isHovered, isSelected: renderProps.isSelected })}>
                {label}
              </span>
            )}
          </span>
        );

        if (!isVertical) return surface;

        return (
          <>
            {surface}
            <span className={slots.caption({ isHovered: renderProps.isHovered, isSelected: renderProps.isSelected })}>
              {label}
            </span>
          </>
        );
      }}
    </RACToggleButton>
  );
}
